import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";

const routeSets = {
  default: {
    title: "Popular Heathrow journeys",
    airport: "LHR",
    roads: ["M25", "M4"],
    stations: ["PAD", "STP"],
    prices: [
      { label: "London to Heathrow", from: "Central London", postcode: "W1", price: "from £65", note: "M4 / M25 aware" },
      { label: "Slough to Heathrow", from: "Slough", postcode: "SL1", price: "from £32", note: "Local Heathrow run" },
      { label: "Reading to Heathrow", from: "Reading", postcode: "RG1", price: "from £95", note: "M4 corridor" },
      { label: "Oxford to Heathrow", from: "Oxford", postcode: "OX1", price: "from £145", note: "M40 / M25 route" },
      { label: "Milton Keynes to Heathrow", from: "Milton Keynes", postcode: "MK9", price: "from £155", note: "M1 / M25 route" },
      { label: "Southampton to Heathrow", from: "Southampton", postcode: "SO14", price: "from £165", note: "M3 / M25 route" },
    ],
    intelligence: [
      { type: "road", label: "Road", title: "M25 + M4 route pressure", status: "Latest check", detail: "Main Heathrow corridors watched for delays." },
      { type: "rail", label: "Rail", title: "PAD + STP gateway stations", status: "Monitored", detail: "Default London rail gateways for Heathrow journeys." },
      { type: "airport", label: "Airport", title: "LHR terminals and queues", status: "Live layer", detail: "Airport pressure is checked before quote decisions." },
    ],
  },
  heathrow: {
    title: "Heathrow group transfer preview",
    airport: "LHR",
    roads: ["M4", "M25", "A4"],
    stations: ["LHR", "PAD", "SLO"],
    prices: [
      { label: "Central London to Heathrow", from: "Central London", postcode: "W1", price: "from £65", note: "Hotel and venue runs" },
      { label: "Slough to Heathrow", from: "Slough", postcode: "SL1", price: "from £32", note: "Local Heathrow route" },
      { label: "Windsor to Heathrow", from: "Windsor", postcode: "SL4", price: "from £45", note: "Castle and hotel groups" },
      { label: "Ascot to Heathrow", from: "Ascot", postcode: "SL5", price: "from £62", note: "Racecourse and SL5 groups" },
    ],
    intelligence: [
      { type: "road", label: "Road", title: "M4 / M25 / A4 pressure", status: "Route check", detail: "Heathrow group timing depends on terminal choice and road pressure." },
      { type: "rail", label: "Rail", title: "LHR + PAD + SLO signals", status: "Monitored", detail: "Airport rail and local gateway signals stay visible." },
      { type: "airport", label: "Airport", title: "LHR terminals and queues", status: "Live layer", detail: "Flights, terminals, queues and weather remain checked." },
    ],
  },
  london: {
    title: "London to Heathrow preview",
    roads: ["M4", "M25"],
    stations: ["PAD", "STP"],
    prices: [
      { label: "Paddington to Heathrow", from: "Paddington", postcode: "W2", price: "from £58", note: "West London gateway" },
      { label: "King's Cross to Heathrow", from: "King's Cross", postcode: "N1C", price: "from £72", note: "STP / central route" },
      { label: "Kensington to Heathrow", from: "Kensington", postcode: "SW7", price: "from £62", note: "M4 access" },
      { label: "Canary Wharf to Heathrow", from: "Canary Wharf", postcode: "E14", price: "from £92", note: "Cross-London timing" },
    ],
    intelligence: [
      { type: "road", label: "Road", title: "M4 and M25 watched", status: "Route check", detail: "Best for west and central London Heathrow traffic." },
      { type: "rail", label: "Rail", title: "PAD / STP monitored", status: "Gateway rail", detail: "Paddington and St Pancras are useful rail disruption signals." },
      { type: "airport", label: "Airport", title: "LHR queues + terminals", status: "Live layer", detail: "Terminal and queue pressure stays visible." },
    ],
  },
  "heathrow-london": {
    title: "Heathrow to London group transfer preview",
    airport: "LHR",
    roads: ["M4", "A4", "M25"],
    stations: ["LHR", "PAD", "EAL"],
    prices: [
      { label: "Heathrow to Paddington", from: "Heathrow Airport", postcode: "W2", price: "from £95", note: "West London gateway" },
      { label: "Heathrow to Kensington", from: "Heathrow Airport", postcode: "SW7", price: "from £98", note: "Hotels and museums" },
      { label: "Heathrow to King's Cross", from: "Heathrow Airport", postcode: "N1C", price: "from £115", note: "Central rail connection" },
      { label: "Heathrow to Canary Wharf", from: "Heathrow Airport", postcode: "E14", price: "from £135", note: "Cross-London journey" },
    ],
    intelligence: [
      { type: "road", label: "Road", title: "M4 / A4 / M25 pressure", status: "Route check", detail: "London travel time changes significantly by destination and time of day." },
      { type: "rail", label: "Rail", title: "LHR / PAD / Ealing signals", status: "Monitored", detail: "Elizabeth line and Heathrow Express provide useful disruption context." },
      { type: "airport", label: "Airport", title: "LHR terminals and queues", status: "Live layer", detail: "Flight, terminal and queue pressure remain visible before pickup." },
    ],
  },
  reading: {
    title: "Reading to Heathrow preview",
    roads: ["M4"],
    stations: ["RDG", "SLO"],
    prices: [
      { label: "Reading RG1 to Heathrow", from: "Reading", postcode: "RG1", price: "from £95", note: "Central Reading" },
      { label: "Caversham RG4 to Heathrow", from: "Caversham", postcode: "RG4", price: "from £98", note: "North Reading" },
      { label: "Earley RG6 to Heathrow", from: "Earley", postcode: "RG6", price: "from £92", note: "East Reading" },
    ],
    intelligence: [
      { type: "road", label: "Road", title: "M4 corridor", status: "Route check", detail: "Reading to Heathrow timing depends heavily on the M4." },
      { type: "rail", label: "Rail", title: "RDG + SLO stations", status: "Monitored", detail: "Reading and Slough rail disruption signals are relevant." },
      { type: "airport", label: "Airport", title: "LHR live status", status: "Live layer", detail: "Flights, terminals and queues remain checked." },
    ],
  },
  slough: {
    title: "Slough to Heathrow preview",
    roads: ["M4"],
    stations: ["SLO", "PAD"],
    prices: [
      { label: "Slough SL1 to Heathrow", from: "Slough", postcode: "SL1", price: "from £32", note: "Central Slough" },
      { label: "Langley SL3 to Heathrow", from: "Langley", postcode: "SL3", price: "from £30", note: "Near T5 / M4" },
      { label: "Burnham SL1 to Heathrow", from: "Burnham", postcode: "SL1", price: "from £36", note: "West Slough" },
    ],
    intelligence: [
      { type: "road", label: "Road", title: "M4 local corridor", status: "Route check", detail: "Short Heathrow runs still depend on M4 pressure." },
      { type: "rail", label: "Rail", title: "SLO station", status: "Monitored", detail: "Slough rail status is a useful local signal." },
      { type: "airport", label: "Airport", title: "LHR terminals", status: "Live layer", detail: "Airport pressure remains the main destination signal." },
    ],
  },
  maidenhead: {
    title: "Maidenhead to Heathrow preview",
    roads: ["M4"],
    stations: ["MAI", "SLO", "PAD"],
    prices: [
      { label: "Maidenhead SL6 to Heathrow", from: "Maidenhead", postcode: "SL6", price: "from £58", note: "Central Maidenhead" },
      { label: "Cookham to Heathrow", from: "Cookham", postcode: "SL6", price: "from £62", note: "North Maidenhead" },
      { label: "Bray to Heathrow", from: "Bray", postcode: "SL6", price: "from £56", note: "M4 corridor" },
    ],
    intelligence: [
      { type: "road", label: "Road", title: "M4 corridor", status: "Route check", detail: "Maidenhead to Heathrow timing depends heavily on the M4." },
      { type: "rail", label: "Rail", title: "MAI + SLO stations", status: "Monitored", detail: "Maidenhead and Slough rail disruption signals are relevant." },
      { type: "airport", label: "Airport", title: "LHR terminals", status: "Live layer", detail: "Flights, terminals and queues remain checked." },
    ],
  },
  windsor: {
    title: "Windsor to Heathrow preview",
    roads: ["M4", "M25", "A308"],
    stations: ["WNC", "WNR", "SLO"],
    prices: [
      { label: "Windsor SL4 to Heathrow", from: "Windsor", postcode: "SL4", price: "from £45", note: "Town centre / Castle area" },
      { label: "Eton to Heathrow", from: "Eton", postcode: "SL4", price: "from £48", note: "Windsor riverside" },
      { label: "LEGOLAND Windsor to Heathrow", from: "LEGOLAND Windsor", postcode: "SL4", price: "from £52", note: "Family luggage route" },
    ],
    intelligence: [
      { type: "road", label: "Road", title: "M4 / M25 / A308 route", status: "Route check", detail: "Windsor to Heathrow timing depends on terminal choice and local road pressure." },
      { type: "rail", label: "Rail", title: "Windsor + Slough stations", status: "Monitored", detail: "Windsor & Eton stations and Slough rail signals are relevant." },
      { type: "airport", label: "Airport", title: "LHR terminals", status: "Live layer", detail: "Flights, terminals and queues remain checked." },
    ],
  },
  ascot: {
    title: "Ascot to Heathrow preview",
    roads: ["M4", "M25", "A329"],
    stations: ["ACT", "SNG", "SLO"],
    prices: [
      { label: "Ascot SL5 to Heathrow", from: "Ascot", postcode: "SL5", price: "from £62", note: "Town centre / racecourse" },
      { label: "Sunninghill to Heathrow", from: "Sunninghill", postcode: "SL5", price: "from £64", note: "Ascot west" },
      { label: "Sunningdale to Heathrow", from: "Sunningdale", postcode: "SL5", price: "from £68", note: "South Ascot route" },
    ],
    intelligence: [
      { type: "road", label: "Road", title: "M4 / M25 route pressure", status: "Route check", detail: "Ascot to Heathrow timing depends on terminal choice, racecourse traffic and road pressure." },
      { type: "rail", label: "Rail", title: "Ascot + Sunningdale stations", status: "Monitored", detail: "Ascot and Sunningdale rail signals are relevant during event movement." },
      { type: "airport", label: "Airport", title: "LHR terminals", status: "Live layer", detail: "Flights, terminals and queues remain checked." },
    ],
  },
  henley: {
    title: "Henley to Heathrow preview",
    roads: ["M4", "M40", "A4130"],
    stations: ["HOT", "RDG", "SLO"],
    prices: [
      { label: "Henley RG9 to Heathrow", from: "Henley-on-Thames", postcode: "RG9", price: "from £82", note: "Town centre / station" },
      { label: "Henley Regatta to Heathrow", from: "Henley Royal Regatta", postcode: "RG9 2LP", price: "from £88", note: "Event pickup planning" },
      { label: "Wargrave to Heathrow", from: "Wargrave", postcode: "RG10", price: "from £78", note: "Thames Valley route" },
    ],
    intelligence: [
      { type: "road", label: "Road", title: "M4 / M40 / A4130 route", status: "Route check", detail: "Henley to Heathrow timing depends on Regatta traffic, M4 or M40 choice and terminal approach." },
      { type: "rail", label: "Rail", title: "Henley + Reading stations", status: "Monitored", detail: "Henley-on-Thames, Reading and Slough rail signals are useful local context." },
      { type: "airport", label: "Airport", title: "LHR terminals", status: "Live layer", detail: "Flights, terminals and queues remain checked." },
    ],
  },
  oxford: {
    title: "Oxford to Heathrow preview",
    roads: ["M40", "M25"],
    stations: ["OXF", "PAD", "SLO"],
    prices: [
      { label: "Oxford OX1 to Heathrow", from: "Oxford", postcode: "OX1", price: "from £145", note: "City centre" },
      { label: "Summertown OX2 to Heathrow", from: "Summertown", postcode: "OX2", price: "from £148", note: "North Oxford" },
      { label: "Cowley OX4 to Heathrow", from: "Cowley", postcode: "OX4", price: "from £142", note: "East Oxford" },
    ],
    intelligence: [
      { type: "road", label: "Road", title: "M40 + M25 route", status: "Route check", detail: "Oxford transfer timing is driven by M40/M25 pressure." },
      { type: "rail", label: "Rail", title: "Oxford + Slough/PAD", status: "Monitored", detail: "Rail alternatives usually touch Oxford, Paddington or Slough." },
      { type: "airport", label: "Airport", title: "LHR live status", status: "Live layer", detail: "Destination queues and flights stay visible." },
    ],
  },
};

const vehicleMultipliers = {
  taxi: 1,
  minibus: 1.65,
  coach: 2.7,
};

const vehicleLabels = {
  taxi: "Taxi preview",
  minibus: "Minibus preview",
  coach: "Coach preview",
};

const liveVehicleConfig = {
  taxi: { passengers: 2, bags: 2, preferredCodes: ["saloon", "estate", "mpv"] },
  minibus: { passengers: 8, bags: 6, preferredCodes: ["nine", "fourteen"] },
  coach: { passengers: 25, bags: 10, preferredCodes: ["twentyfive"] },
};

const PREVIEW_CACHE_TTL_MS = 2 * 24 * 60 * 60 * 1000;
const PREVIEW_CACHE_PREFIX = "route-preview2-price-v1";

const parsePrice = (price) => {
  const match = String(price || "").match(/(\d+)/);
  return match ? Number(match[1]) : null;
};

const formatPreviewPrice = (basePrice, vehicle) => {
  const number = parsePrice(basePrice);
  if (!number || vehicle === "taxi") return basePrice;
  const adjusted = Math.round((number * (vehicleMultipliers[vehicle] || 1)) / 5) * 5;
  return `from £${adjusted}`;
};

const resolveRouteSet = (routeKey) => routeSets[routeKey] || routeSets.default;

const previewPickupTime = () => {
  const pickup = new Date(Date.now() + 49 * 60 * 60 * 1000);
  pickup.setMinutes(pickup.getMinutes() - pickup.getTimezoneOffset());
  return pickup.toISOString().slice(0, 16);
};

const cacheKeyFor = (item, vehicle) =>
  `${PREVIEW_CACHE_PREFIX}:${vehicle}:${item.postcode || item.from || item.label}:TW6`;

const readCachedPreview = (item, vehicle) => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(cacheKeyFor(item, vehicle));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > PREVIEW_CACHE_TTL_MS) return null;
    return parsed.value || null;
  } catch {
    return null;
  }
};

const writeCachedPreview = (item, vehicle, value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      cacheKeyFor(item, vehicle),
      JSON.stringify({ savedAt: Date.now(), value })
    );
  } catch {
    // Local storage is a speed hint only; ignore quota/privacy failures.
  }
};

const selectVehiclePreview = (vehicles, vehicle) => {
  const list = Array.isArray(vehicles) ? vehicles : [];
  const preferredCodes = liveVehicleConfig[vehicle]?.preferredCodes || [];
  return (
    preferredCodes.map((code) => list.find((candidate) => candidate?.code === code && Number(candidate?.price) > 0)).find(Boolean) ||
    list.find((candidate) => candidate?.available && Number(candidate?.price) > 0) ||
    list.find((candidate) => Number(candidate?.price) > 0)
  );
};

const formatVehicleMeta = (selected) => {
  if (!selected) return "Engine estimate";
  const isFixed = selected.price_type === "fixed";
  const isLive = selected.book_mode === "live";
  if (isLive && isFixed) return "Live fixed price";
  if (isLive) return "Live engine estimate";
  if (selected.book_mode === "enquiry") return "Engine estimate";
  return isFixed ? "Fixed preview" : "Engine estimate";
};

const buildLiveIntelligenceItems = (set, airport) => {
  if (!airport) return [];
  const hero = airport.hero_status || {};
  const preview = airport.preview || {};
  const road = hero.road || {};
  const rail = hero.rail || {};
  const flights = hero.flights || {};
  const overall = airport.overall || {};
  const roadTitle = `${(set.roads || ["M25", "M4"]).join(" + ")} route pressure`;
  const railTitle = `${(set.stations || ["PAD", "STP"]).join(" + ")} rail signals`;
  const flightDetail =
    flights.headline ||
    preview?.flights?.headline ||
    preview?.arrivals_pressure?.headline ||
    "Flights, terminals and queues are checked for Heathrow.";

  return [
    {
      type: "road",
      label: "Road",
      title: roadTitle,
      status: road.label || "Live check",
      detail: road.headline || road.summary || "Live road pressure is checked for the Heathrow route.",
    },
    {
      type: "rail",
      label: "Rail",
      title: railTitle,
      status: rail.label || "Live check",
      detail: rail.headline || rail.summary || "Rail and Underground disruption signals are monitored.",
    },
    {
      type: "airport",
      label: "Airport",
      title: `${set.airport || "LHR"} terminals, flights and queues`,
      status: overall.label || flights.label || "Live",
      detail: flightDetail,
    },
  ];
};

const quoteHref = (item, vehicle) => {
  const params = new URLSearchParams({
    pickup: item.postcode || item.from || "",
    dropoff: "TW6",
    preview2: "1",
    vehicle,
  });
  return `/?${params.toString()}#${vehicle === "coach" ? "cheapcoachgrab-quote-form" : "minibusgrab-quote-form"}`;
};

export function RoutePricePreview2({ routeKey = "default", vehicle = "taxi", limit = 4, title }) {
  const set = resolveRouteSet(routeKey);
  const rows = useMemo(() => (set.prices || []).slice(0, limit), [set, limit]);
  const [livePreviews, setLivePreviews] = useState({});

  useEffect(() => {
    if (!rows.length) return undefined;
    let cancelled = false;
    const config = liveVehicleConfig[vehicle] || liveVehicleConfig.taxi;

    rows.forEach((item) => {
      const cached = readCachedPreview(item, vehicle);
      if (cached) {
        setLivePreviews((current) => ({ ...current, [item.label]: cached }));
        return;
      }

      api
        .post("/api/quote-preview", {
          pickup: item.postcode || item.from,
          dropoff: "TW6",
          passengers: config.passengers,
          bags: config.bags,
          pickup_time: previewPickupTime(),
        })
        .then((res) => {
          const selected = selectVehiclePreview(res?.data?.vehicles, vehicle);
          const price = Number(selected?.price);
          if (!Number.isFinite(price) || price <= 0) return;
          const value = {
            price: `from £${Math.round(price)}`,
            note: formatVehicleMeta(selected),
            vehicleLabel: selected?.label || "",
          };
          writeCachedPreview(item, vehicle, value);
          if (!cancelled) {
            setLivePreviews((current) => ({ ...current, [item.label]: value }));
          }
        })
        .catch(() => {
          // Static route estimates stay visible if preview pricing is unavailable.
        });
    });

    return () => {
      cancelled = true;
    };
  }, [rows, vehicle]);

  if (!rows.length) return null;

  return (
    <section className="route-preview2 route-preview2-prices" aria-label="Popular route price previews">
      <div className="route-preview2-head">
        <span>{vehicleLabels[vehicle] || "Price preview"}</span>
        <strong>{title || set.title}</strong>
        <small>Live engine preview • cached 2 days</small>
      </div>
      <div className="route-preview2-row">
        {rows.map((item) => {
          const live = livePreviews[item.label];
          return (
            <a className="route-preview2-chip" href={quoteHref(item, vehicle)} key={`${item.label}-${vehicle}`}>
              <span>
                <b>{item.label}</b>
                <small>{live?.note || item.note}</small>
              </span>
              <strong>{live?.price || formatPreviewPrice(item.price, vehicle)}</strong>
            </a>
          );
        })}
      </div>
    </section>
  );
}

export function RouteIntelligencePreview2({ routeKey = "default", compact = false }) {
  const set = resolveRouteSet(routeKey);
  const [liveItems, setLiveItems] = useState([]);
  const items = liveItems.length ? liveItems : set.intelligence || [];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/api/open/airport-intelligence-summary?airports=${encodeURIComponent(set.airport || "LHR")}`)
      .then((res) => {
        const airports = Array.isArray(res?.data?.airports) ? res.data.airports : [];
        const airport = airports.find((entry) => entry?.code === (set.airport || "LHR")) || airports[0];
        const nextItems = buildLiveIntelligenceItems(set, airport);
        if (!cancelled && nextItems.length) {
          setLiveItems(nextItems);
          setIndex(0);
        }
      })
      .catch(() => {
        if (!cancelled) setLiveItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [set]);

  useEffect(() => {
    if (items.length <= 1) return undefined;
    const timer = setInterval(() => {
      setIndex((current) => (current + 1) % items.length);
    }, 3800);
    return () => clearInterval(timer);
  }, [items.length]);

  if (!items.length) return null;
  const active = items[index % items.length];

  return (
    <section className={`route-preview2 route-preview2-intel ${compact ? "is-compact" : ""}`} aria-label="Route intelligence preview">
      <div className="route-preview2-intel-card" key={`${active.type}-${index}`}>
        <span className={`route-preview2-icon is-${active.type}`}>{active.label}</span>
        <strong>{active.title}</strong>
        <p>{active.detail}</p>
        <em>{active.status}</em>
      </div>
    </section>
  );
}

export { routeSets as routePreview2Config };
